import { PHASE } from "../../common/constants.ts";
import { idb } from "../db/index.ts";
import { getActiveSeasonSelector } from "../db/merged/players.ts";
import { g, helpers } from "../util/index.ts";
import type {
	Player,
	PlayerInjury,
	PlayerStatAttr,
	PlayerStatType,
} from "../../common/types.ts";
import { defineView } from "../util/defineView.ts";
import { range } from "../../common/utils.ts";
import addFirstNameShort from "../util/addFirstNameShort.ts";
import { player, season, team } from "../core/index.ts";
import { extraStats } from "./hallOfFame.ts";
import { bySport } from "../../common/sportFunctions.ts";
import { processPlayersHallOfFame } from "../util/processPlayersHallOfFame.ts";
import { defaultGameAttributes } from "../../common/defaultGameAttributes.ts";
import { getLeaderRequirementsStats } from "../core/season/getLeaderRequirements.ts";
import type { RouteParams } from "../../ui/router/types.ts";
import { validateSeason } from "../util/processInputs.ts";
import { validateSeasonType } from "../util/processInputs.ts";
import { validateStatType } from "../util/processInputs.ts";

const processInputs = (params: RouteParams<"leaders">) => {
	let season: "career" | "all" | number;
	if (params.season === "career" || params.season === "all") {
		season = params.season;
	} else {
		season = validateSeason(params.season);
	}

	return {
		season,
		playoffs: validateSeasonType(params.playoffs),
		statType: validateStatType(params.statType),
	};
};

export const getCategoriesAndStats = (onlyStat?: string) => {
	let categories = bySport<
		{
			titleOverride?: string;
			stat: PlayerStatAttr;
			minStats?: Partial<Record<PlayerStatAttr, number>>;
			sortAscending?: true;
			filter?: (p: unknown) => boolean;
		}[]
	>({
		baseball: [
			{
				stat: "ba",
			},
			{
				stat: "hr",
			},
			{
				stat: "rbi",
			},
			{
				stat: "r",
			},
			{
				stat: "h",
			},
			{
				stat: "2b",
			},
			{
				stat: "3b",
			},
			{
				stat: "sb",
			},
			{
				stat: "bb",
			},
			{
				stat: "obp",
			},
			{
				stat: "slg",
			},
			{
				stat: "ops",
			},
			{
				stat: "tb",
			},
			{
				stat: "w",
			},
			{
				stat: "l",
			},
			{
				stat: "era",
			},
			{
				stat: "qs",
			},
			{
				stat: "sho",
			},
			{
				stat: "sv",
			},
			{
				stat: "ip",
			},
			{
				stat: "hrPit",
			},
			{
				stat: "bbPit",
			},
			{
				stat: "soPit",
			},
			{
				stat: "pc",
			},
			{
				stat: "bk",
			},
			{
				stat: "wp",
			},
			{
				stat: "bf",
			},
			{
				stat: "fip",
			},
			{
				stat: "whip",
			},
			{
				stat: "h9",
			},
			{
				stat: "hr9",
			},
			{
				stat: "bb9",
			},
			{
				stat: "so9",
			},
			{
				stat: "pc9",
			},
			{
				stat: "sow",
			},
			{
				stat: "rbat",
			},
			{
				stat: "rbr",
			},
			{
				stat: "rfldTot",
			},
			{
				stat: "rpit",
			},
			{
				stat: "war",
			},
		],
		basketball: [
			{
				stat: "pts",
			},
			{
				stat: "trb",
			},
			{
				stat: "ast",
			},
			{
				stat: "fg",
			},
			{
				stat: "fga",
			},
			{
				stat: "fgp",
			},
			{
				stat: "tp",
			},
			{
				stat: "tpa",
			},
			{
				stat: "tpp",
			},
			{
				stat: "ft",
			},
			{
				stat: "fta",
			},
			{
				stat: "ftp",
			},
			{
				stat: "blk",
			},
			{
				stat: "stl",
			},
			{
				stat: "min",
			},
			{
				stat: "per",
			},
			{
				stat: "ewa",
			},
			{
				stat: "ws48",
				titleOverride: "Win Shares / 48 Mins",
			},
			{
				stat: "ows",
			},
			{
				stat: "dws",
			},
			{
				stat: "ws",
			},
			{
				stat: "obpm",
			},
			{
				stat: "dbpm",
			},
			{
				stat: "bpm",
			},
			{
				stat: "vorp",
			},
			{
				stat: "onOff100",
				titleOverride: "+/- Per 100, On-Off",
			},
		],
		football: [
			{
				stat: "pssYds",
			},
			{
				stat: "pssYdsPerAtt",
			},
			{
				stat: "cmpPct",
			},
			{
				stat: "pssTD",
				titleOverride: "Passing TDs",
			},
			{
				stat: "pssInt",
			},
			{
				stat: "qbRat",
			},
			{
				stat: "rusYds",
			},
			{
				stat: "rusYdsPerAtt",
			},
			{
				stat: "rusTD",
				titleOverride: "Rushing TDs",
			},
			{
				stat: "recYds",
			},
			{
				stat: "recYdsPerRec",
			},
			{
				stat: "recTD",
				titleOverride: "Receiving TDs",
			},
			{
				stat: "ydsFromScrimmage",
				titleOverride: "Yards From Scrimmage",
			},
			{
				stat: "rusRecTD",
				titleOverride: "Rushing and Receiving TDs",
			},
			{
				stat: "pbwr",
			},
			{
				stat: "rbwr",
			},
			{
				stat: "skAlw",
			},
			{
				stat: "defTck",
			},
			{
				stat: "defSk",
			},
			{
				stat: "defInt",
			},
			{
				stat: "defFmbFrc",
			},
			{
				stat: "defFmbRec",
			},
			{
				stat: "av",
			},
		],
		hockey: [
			{
				stat: "g",
			},
			{
				stat: "a",
			},
			{
				stat: "pts",
			},
			{
				stat: "pm",
			},
			{
				stat: "pim",
			},
			{
				stat: "min",
			},
			{
				stat: "blk",
			},
			{
				stat: "hit",
			},
			{
				stat: "tk",
			},
			{
				stat: "gv",
			},
			{
				stat: "svPct",
			},
			{
				stat: "gaa",
			},
			{
				stat: "so",
			},
			{
				stat: "gc",
			},
			{
				stat: "ops",
			},
			{
				stat: "dps",
			},
			{
				stat: "gps",
			},
			{
				stat: "ps",
			},
		],
	});

	if (onlyStat) {
		categories = categories.filter((row) => row.stat === onlyStat);
		if (categories.length === 0) {
			throw new Error(`Invalid stat "${onlyStat}"`);
		}
	}

	const leaderRequirements = season.getLeaderRequirements();

	const augmentedCategories = categories.map((category) => {
		if (leaderRequirements[category.stat]) {
			return {
				...category,
				...leaderRequirements[category.stat],
			};
		}

		return category;
	});

	const actualStats = augmentedCategories.map((row) => row.stat);
	const stats = Array.from(
		new Set([
			...actualStats,
			...getLeaderRequirementsStats(leaderRequirements, actualStats),
		]),
	);

	return {
		categories: augmentedCategories,
		stats,
	};
};

// Calculate the number of games played for each team, which is used to test if a player qualifies as a league leader
export class GamesPlayedCache {
	regularSeasonCache: Record<number, Record<number, number>>;
	playoffsCache: Record<number, Record<number, number>>;

	constructor() {
		this.regularSeasonCache = {};
		this.playoffsCache = {};
	}

	private straightNumGames(season: number, playoffs: boolean) {
		// Current year, regular season, completed season -> we already know how many games each team played, from numGames
		return (
			!playoffs &&
			season === g.get("season") &&
			g.get("phase") >= PHASE.PLAYOFFS
		);
	}

	async loadSeasons(seasons: number[], playoffs: boolean) {
		for (const season of seasons) {
			if (this.straightNumGames(season, playoffs)) {
				continue;
			}

			// In the playoffs, if we're in the first round or later (not the play-in), set the number of games to the minimum number of games to win a first round series (or the number of games so far). That way you won't see someone who played like 1 game in the play-in tournament leading in points per game after the entire playoffs.
			let minGpPlayoffs = 0;
			if (playoffs) {
				const playoffSeries = await idb.merged.playoffSeries.get(season);

				// This only matters if there is a play-in tournament
				if (playoffSeries?.playIns) {
					// Find any non-bye first round series and see how many games have been played so far
					for (const series of playoffSeries.series[0]!) {
						if (series.away) {
							// Number of games played so far - can't require more than this!
							const numGamesFirstRoundSoFar = series.home.won + series.away.won;

							// Number of games in a first round sweep
							const numGamesMinAfterFirstRound = helpers.numGamesToWinRound(
								0,
								season,
							);

							minGpPlayoffs = Math.min(
								numGamesFirstRoundSoFar,
								numGamesMinAfterFirstRound,
							);

							// No need to look for more, since series schedules are always in sync (all series play on same days)
							break;
						}
					}
				}
			}

			const teams = await team.getFormattedAll({
				attrs: ["tid"],
				stats: ["gp"], // Use this only for playoffs, since seasonAttrs.gp does not increment for playoffs
				seasonAttrs: ["gp"], // Use seasonAttrs.gp rather than stats.gp for real player historical data which has no team stats
				season,
				playoffs,
				regularSeason: !playoffs,

				// This is needed if teamStats row is deleted but teamSeason is still there, such as historical real players seasons
				showNoStats: true,
			});

			const cache: Record<number, number> = {};
			for (const t of teams) {
				if (playoffs) {
					cache[t.tid] = Math.max(minGpPlayoffs, t.stats.gp);
				} else {
					cache[t.tid] = t.seasonAttrs.gp;
				}
			}

			if (playoffs) {
				this.playoffsCache[season] = cache;
			} else {
				this.regularSeasonCache[season] = cache;
			}
		}
	}

	get(
		season: number,
		type: "regularSeason" | "playoffs" | "combined",
		tid: number,
		career: boolean,
	): number {
		if (type === "combined") {
			return (
				this.get(season, "regularSeason", tid, career) +
				this.get(season, "playoffs", tid, career)
			);
		}

		const playoffs = type === "playoffs";

		if (career) {
			if (playoffs) {
				// Arbitrary - two full playoffs runs
				const numGamesPlayoffSeries = g.get("numGamesPlayoffSeries", season);
				let sum = 0;
				for (const games of numGamesPlayoffSeries) {
					sum += games;
				}
				return 2 * sum;
			}

			// Arbitrary - 4 seasons, scaled to the number of seasons played
			const numSeasons = Math.min(1 + season - g.get("startingSeason"), 4);
			let numGames;
			if (this.regularSeasonCache[season]) {
				numGames = Math.max(...Object.values(this.regularSeasonCache[season]));
			} else {
				numGames = g.get("numGames");
			}
			return numGames * numSeasons;
		}

		if (this.straightNumGames(season, playoffs)) {
			return g.get("numGames");
		}

		// Fallback will happen if there is missing data or if it's realRosters for a past season. For the latter case, we actually do have the correct historical values in g, which mostly works. Same is true for some other cases with missing data, probably. The regularSeason estimate is better than playoffs, because we don't know how many rounds a team played in the playoffs (at least without doing more work, like looking in the playoffSeries database, and whatever who cares).
		if (playoffs) {
			const gp = this.playoffsCache[season]?.[tid];
			if (gp !== undefined) {
				return gp;
			}

			return helpers.numGamesToWinRound(0, season);
		}

		return this.regularSeasonCache[season]?.[tid] ?? g.get("numGames", season);
	}
}

// cb runs inside an IndexedDB transaction, so it must be synchronous
export const iterateAllPlayersWithStats = async (
	season: number | "all" | "career",
	cb: (p: Player, season: number | "career") => void,
) => {
	await idb.merged.players.forEach(
		typeof season === "number" ? getActiveSeasonSelector(season) : undefined,
		(p) => {
			if (season === "all") {
				const seasons = new Set(p.stats.map((row) => row.season));
				for (const season of seasons) {
					cb(p, season);
				}
			} else if (season === "career") {
				// No need to look at career stats of player with no stats
				if (p.stats.length > 0) {
					cb(p, season);
				}
			} else if (p.stats.some((row) => row.season === season)) {
				cb(p, season);
			}
		},
	);
};

type Category = ReturnType<typeof getCategoriesAndStats>["categories"][number];

// Accept season even when career is true, because for progressive leaders we need to know the season being evaluated, otherwise we might want multiple seasons of stats to determine the all-time leader when there is only one season available
export const playerMeetsCategoryRequirements = ({
	career,
	cat,
	gamesPlayedCache,
	p,
	playerStats,
	seasonType,
	season,
	statType,
}: {
	career: boolean;
	cat: Category;
	gamesPlayedCache: GamesPlayedCache;
	p: unknown;
	playerStats: Record<string, any>;
	seasonType: "regularSeason" | "playoffs" | "combined";
	season: number;
	statType: PlayerStatType;
}) => {
	const numPlayersOnCourtFactor = bySport({
		baseball: 1,
		basketball:
			defaultGameAttributes.numPlayersOnCourt / g.get("numPlayersOnCourt"),
		football: 1,
		hockey: 1,
	});

	// To handle changes in number of games, playing time, etc
	const factor =
		(g.get("numGames") / defaultGameAttributes.numGames[0].value) *
		numPlayersOnCourtFactor *
		helpers.quarterLengthFactor();

	// Need to undo numPlayersOnCourtFactor for testing "min", because you don't get more minutes with less players on the court, unlike most other stats
	const minFactor = 1 / numPlayersOnCourtFactor;

	// Test if the player meets the minimum statistical requirements for this category
	let pass = !cat.minStats && (!cat.filter || cat.filter(p));

	if (!pass && cat.minStats) {
		for (const [minStat, minValue] of Object.entries(cat.minStats)) {
			// In basketball, everything except gp is a per-game average, so we need to scale them by games played
			let playerValue;
			if (
				__SPORT !== "basketball" ||
				minStat === "gp" ||
				statType === "totals"
			) {
				playerValue = playerStats[minStat];
			} else if (statType === "per36") {
				playerValue =
					(playerStats[minStat] * playerStats.gp * playerStats.min) / 36;
			} else {
				playerValue = playerStats[minStat] * playerStats.gp;
			}

			if (playerValue === undefined || Number.isNaN(playerValue)) {
				// Value is missing, could be custom stats row in a file missing something or historical data with null values
				continue;
			}

			const gpTeam = gamesPlayedCache.get(
				season,
				seasonType,
				playerStats.tid,
				career,
			);

			// Special case GP
			if (minStat === "gp") {
				if (
					playerValue / gpTeam >=
					minValue / defaultGameAttributes.numGames[0].value
				) {
					pass = true;
					break; // If one is true, don't need to check the others
				}
			} else {
				// Other stats
				if (
					playerValue >=
					Math.ceil(
						(minValue * factor * (minStat === "min" ? minFactor : 1) * gpTeam) /
							g.get("numGames"),
					)
				) {
					pass = true;
					break; // If one is true, don't need to check the others
				}
			}
		}
	}

	// Unless we're sorting ascending, ignore value of 0 because that could mean missing data
	if (pass && !cat.sortAscending && playerStats[cat.stat] === 0) {
		pass = false;
	}

	return pass;
};

export type Leader = {
	abbrev: string;
	hof: boolean;
	injury: PlayerInjury | undefined;
	jerseyNumber: string | undefined;
	key: number | string;
	firstName: string;
	firstNameShort?: string;
	lastName: string;
	pid: number;
	pos: string;
	retiredYear: number;
	season: number | undefined;
	stat: number;
	skills: string[] | undefined;
	tid: number;
	userTeam: boolean;
	watch: number;
};

export const leadersAddFirstNameShort = <
	LocalLeader extends {
		firstName: string;
		lastName: string;
	},
	T extends {
		leaders: LocalLeader[];
	},
>(
	rows: T[],
) =>
	rows.map((row) => ({
		...row,
		leaders: addFirstNameShort(row.leaders),
	}));

const NUM_LEADERS = 10;

export default defineView({
	id: "leaders",
	processInputs,
	load: async ({ inputs, updateEvents, prevInputs }) => {
		if (
			updateEvents.has("firstRun") ||
			(inputs.season === g.get("season") && updateEvents.has("gameSim")) ||
			inputs.season !== prevInputs?.season ||
			inputs.playoffs !== prevInputs?.playoffs ||
			inputs.statType !== prevInputs?.statType
		) {
			const { categories, stats } = getCategoriesAndStats();

			const outputCategories = categories.map((category) => ({
				titleOverride: category.titleOverride,
				stat: category.stat,
				leaders: [] as Leader[],
			}));

			// Load all gameslayedCache seasons ahead of time, so we don't make IndexedDB transaction auto commit if doing this dynamically in iterateAllPlayers
			const gamesPlayedCache = new GamesPlayedCache();
			let seasons: number[];
			if (inputs.season === "career") {
				// Nothing to cache
				seasons = [];
			} else if (inputs.season === "all") {
				seasons = range(g.get("startingSeason"), g.get("season") + 1);
			} else {
				seasons = [inputs.season];
			}

			if (inputs.playoffs === "combined") {
				await gamesPlayedCache.loadSeasons(seasons, false);
				await gamesPlayedCache.loadSeasons(seasons, true);
			} else {
				await gamesPlayedCache.loadSeasons(
					seasons,
					inputs.playoffs === "playoffs",
				);
			}

			const attrs = [
				"pid",
				"firstName",
				"lastName",
				"injury",
				"watch",
				"jerseyNumber",
				"hof",
				"retiredYear",
			] as const;
			const allStats = ["abbrev", "tid", ...stats, ...extraStats] as const;

			const careerFormatOptions = {
				attrs,
				// season and ovr only needed for bestPos
				ratings: ["season", "ovr", "skills", "pos"],
				stats: allStats,
				seasonType: inputs.playoffs,
				mergeStats: "totOnly",
				statType: inputs.statType,
			} as const;

			// Load abbrevs ahead of time, so there is no database access while iterating over players, which would make the IndexedDB transaction auto commit. For a single season, only load abbrevs for that season. Otherwise, the career options (with no season) load abbrevs for every season, which are shared by the career formatter and all the single season formatters.
			const abbrevs = await player.loadAbbrevs(
				typeof inputs.season === "number"
					? { ...careerFormatOptions, season: inputs.season }
					: careerFormatOptions,
			);

			const formatCareer = player.createFormatter(careerFormatOptions, abbrevs);

			const createSeasonFormatter = (season: number) =>
				player.createFormatter(
					{
						attrs,
						ratings: ["skills", "pos"],
						stats: allStats,
						season,
						seasonType: inputs.playoffs,
						mergeStats: "totOnly",
						statType: inputs.statType,
					},
					abbrevs,
				);
			const seasonFormatters = new Map<
				number,
				ReturnType<typeof createSeasonFormatter>
			>();

			// Returns the player, the stats row to use, and some values that differ between career and single season
			const getPlayerInfo = (pRaw: Player, season: number | "career") => {
				if (season === "career") {
					const p = formatCareer(pRaw);
					if (!p) {
						return;
					}

					const playerStats =
						inputs.playoffs === "playoffs"
							? p.careerStatsPlayoffs
							: inputs.playoffs === "combined"
								? p.careerStatsCombined
								: p.careerStats;
					if (!playerStats) {
						return;
					}

					return {
						p,
						playerStats,
						skills: undefined,
						// Shitty handling of career totals, only computed if needed because it's slow
						getTeamAndPos: () => {
							const { bestPos, legacyTid } = processPlayersHallOfFame([p])[0]!;
							if (legacyTid >= 0) {
								return {
									abbrev: helpers.getAbbrev(legacyTid),
									pos: bestPos,
									tid: legacyTid,
								};
							}
							return {
								abbrev: playerStats.abbrev,
								pos: bestPos,
								tid: playerStats.tid,
							};
						},
					};
				}

				const formatSeason = seasonFormatters.getOrInsertComputed(
					season,
					createSeasonFormatter,
				);
				const p = formatSeason(pRaw);
				if (!p) {
					return;
				}

				return {
					p,
					playerStats: p.stats,
					skills: p.ratings.skills,
					getTeamAndPos: () => ({
						abbrev: p.stats.abbrev,
						pos: p.ratings.pos,
						tid: p.stats.tid,
					}),
				};
			};

			await iterateAllPlayersWithStats(inputs.season, (pRaw, season) => {
				const info = getPlayerInfo(pRaw, season);
				if (!info) {
					return;
				}
				const { p, playerStats, skills, getTeamAndPos } = info;

				for (const [cat, outputCat] of Iterator.zip(
					[categories, outputCategories],
					{ mode: "strict" },
				)) {
					const value = playerStats[cat.stat];
					if (typeof value !== "number") {
						// value should only be undefined in historical data before certain stats were tracked. Leader categories are all numeric stats
						continue;
					}
					const lastValue = outputCat.leaders.at(-1)?.stat;
					if (
						lastValue !== undefined &&
						outputCat.leaders.length >= NUM_LEADERS &&
						((cat.sortAscending && value > lastValue) ||
							(!cat.sortAscending && value < lastValue))
					) {
						// Value is not good enough for the top 10
						continue;
					}

					const pass = playerMeetsCategoryRequirements({
						career: season === "career",
						cat,
						gamesPlayedCache,
						p,
						playerStats,
						seasonType: inputs.playoffs,
						season: season === "career" ? g.get("season") : season,
						statType: inputs.statType,
					});

					if (pass) {
						// Players can appear multiple times if looking at all seasons
						const key = inputs.season === "all" ? `${p.pid}|${season}` : p.pid;

						const { abbrev, pos, tid } = getTeamAndPos();

						const userTid =
							season !== "career" ? g.get("userTid", season) : g.get("userTid");

						const leader = {
							abbrev,
							hof: p.hof,
							injury: p.injury,
							jerseyNumber: p.jerseyNumber,
							key,
							firstName: p.firstName,
							lastName: p.lastName,
							pid: p.pid,
							pos,
							retiredYear: p.retiredYear,
							season:
								inputs.season === "all" && season !== "career"
									? season
									: undefined,
							stat: value,
							skills,
							tid,
							userTeam: userTid === tid,
							watch: p.watch,
						};

						outputCat.leaders = outputCat.leaders.slice(0, NUM_LEADERS - 1);
						outputCat.leaders.push(leader);
						if (cat.sortAscending) {
							outputCat.leaders.sort((a, b) => a.stat - b.stat);
						} else {
							outputCat.leaders.sort((a, b) => b.stat - a.stat);
						}
					}
				}
			});

			const highlightActiveAndHOF =
				inputs.season === "career" ||
				inputs.season === "all" ||
				inputs.season < g.get("season");

			return {
				categories: leadersAddFirstNameShort(outputCategories),
				highlightActiveAndHOF,
				playoffs: inputs.playoffs,
				season: inputs.season,
				statType: inputs.statType,
			};
		}
	},
});
